import React, {useEffect, useState} from 'react';
import Util from '../forecast-app/shared/util/util';
import UpdateCustomFieldValueMutation from '../mutations/update_custom_field_value_mutation';
import {BasicNumberInput, BasicTextInput} from '@/web-components';
import {ComboBox, Text} from '@forecast-it/design-system';
import {CUSTOM_FIELD_TYPE, CustomFieldType} from '../constants';
import {DesignSystemComboBoxBorderAdapter} from '../forecast-app/shared/components/dropdowns/DesignSystemComboBoxOldLookAdapter';
type Option = {
	id: string;
	displayName: string;
};

type CustomFieldValueInputProps = {
	customFieldKey: unknown;
	displayName: unknown;
	value: string | null;
	entityType: unknown;
	entityId: unknown;
	type: CustomFieldType;
	options?: Option[];
	readOnly?: boolean;
};

const GRAPHQL_TYPE_MAP: Record<CustomFieldType, string> = {
	[CUSTOM_FIELD_TYPE.STRING]: 'TextCustomFieldValue',
	[CUSTOM_FIELD_TYPE.DROPDOWN]: 'DropdownCustomFieldValue',
	[CUSTOM_FIELD_TYPE.INTEGER]: 'IntegerCustomFieldValue',
};

const isValidInteger = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+$/.test(val);
};

const integerConverterValue = (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	if (/^-?\d+$/.test(str)) {
		return {float: parseInt(str, 10), full: str, error: false};
	}
	return {float: null, full: str, error: true};
};

export function CustomFieldValueInput({
	customFieldKey,
	displayName,
	value,
	entityType,
	entityId,
	type,
	options = [],
	readOnly,
}: CustomFieldValueInputProps): React.JSX.Element {
	const [currentValue, setCurrentValue] = useState(value);

	useEffect(() => {
		setCurrentValue(value);
	}, [value]);

	const handleCustomFieldChange = (newValue: string | null) => {
		Util.CommitMutation(UpdateCustomFieldValueMutation, {
			__typename: GRAPHQL_TYPE_MAP[type],
			entityId,
			entityType,
			key: customFieldKey,
			value: newValue,
		});
	};

	const handleSave = () => {
		if (value !== currentValue) {
			if (type === CUSTOM_FIELD_TYPE.INTEGER && !isValidInteger(currentValue)) {
				return;
			}
			handleCustomFieldChange(currentValue);
		}
	};

	const onComboBoxChange = (optionId: string) => {
		setCurrentValue(optionId);
		handleCustomFieldChange(optionId);
	};

	if (type === CUSTOM_FIELD_TYPE.DROPDOWN) {
		return (
			<DesignSystemComboBoxBorderAdapter>
				<ComboBox
					data-cy={'custom-field-dropdown'}
					aria-label={displayName + ' dropdown field'}
					defaultItems={options}
					value={currentValue}
					onChange={(optionId: string) => onComboBoxChange(optionId)}
					width={ComboBox.WIDTH.FLEX}
					menuTrigger="focus"
					size={'small'}
				>
					{option => (
						<ComboBox.Item key={atob(option.id).split(':')[1]} textValue={option.displayName}>
							<Text data-cy={'option-' + option.displayName} ellipsis={true} tooltipOnEllipsis={true} size={'2'}>
								{option.displayName}
							</Text>
						</ComboBox.Item>
					)}
				</ComboBox>
			</DesignSystemComboBoxBorderAdapter>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.INTEGER) {
		return (
			<BasicNumberInput
				value={currentValue ?? ''}
				hasError={!readOnly && !isValidInteger(currentValue)}
				onChange={setCurrentValue}
				converterValue={integerConverterValue}
				callback={(floatValue: number | null, e: unknown, isDirty: boolean) => {
					if (!isDirty) return;
					const inputValue = (e as React.FocusEvent<HTMLInputElement>).target.value;
					if (floatValue == null) {
						if (inputValue === '') handleCustomFieldChange(null);
					} else if (isValidInteger(String(floatValue))) {
						handleCustomFieldChange(String(floatValue));
					}
				}}
				disabled={readOnly}
				cy={'custom-field-integer-input'}
			/>
		);
	}

	return <BasicTextInput value={currentValue ?? ''} onChange={setCurrentValue} callback={handleSave} disabled={readOnly} />;
}
